import { useState } from 'react'

import { Camera, Check, Loader2 } from 'lucide-react'
import { FormProvider } from 'react-hook-form'

import { useMissionSubmission } from '../../hooks/useMissionSubmission'

import { Mission } from '@/features/missions/types/mission'
import { useUserProgress } from '@/shared/contexts/UserProgress'
import { FormItem } from '@/shared/form/FormItem'
import { ImageInput } from '@/shared/form/ImageInput'
import { Input } from '@/shared/form/Input'
import { Textarea } from '@/shared/form/Textarea'
import { useMissionsAnalytics } from '@/shared/lib/analytics'
import { showErrorAlert } from '@/shared/lib/ui'
import { cn } from '@/shared/lib/utils'
import { api } from '@/shared/services/api'
import { Box } from '@/shared/ui/Box'
import { Button } from '@/shared/ui/Button'

import { useEvidenceForm } from './hooks'




interface EvidenceFormProps {
  mission: Mission
  onOpenCamera?: () => void
}

export function MissionEvidenceForm({
  mission,
  onOpenCamera
}: EvidenceFormProps) {
  const { refetchProgress } = useUserProgress()
  const { trackMissionCompleted, trackMissionFailed } = useMissionsAnalytics()

  const { submission } = useMissionSubmission({ id: mission.id })

  const { form, evidenceKind, canSubmit } = useEvidenceForm({ mission })

  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmitContent = async (data: any) => {
    if (isSubmitting) return
    setIsSubmitting(true)

    try {
      await api.post(`/user/missions/${mission?.uid}/finish`, {
        record: data
      })

      refetchProgress()

      trackMissionCompleted(mission)
    } catch (error) {
      showErrorAlert(error)

      trackMissionFailed(mission, 'submission_refused')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!mission.confirmation_required) {
    return (
      <div className="space-y-4">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Check className="w-5 h-5 text-green-600" />
            <span className="text-green-800 font-medium">
              Esta missão não requer evidência
            </span>
          </div>
          <p className="text-green-700 text-sm mt-1">
            Clique em &quot;Concluir&quot; para finalizar a missão.
          </p>
        </div>

        {canSubmit && (
          <Button
            onClick={() =>
              handleSubmitContent({
                content: 'Completado sem necessidade de evidência'
              })
            }
            disabled={isSubmitting}
            className="w-full"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Concluindo...
              </>
            ) : (
              <>Concluir</>
            )}
          </Button>
        )}
      </div>
    )
  }

  return (
    <Box className="flex flex-col gap-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Evidência</h3>
        <p className="text-sm text-gray-600 mt-1 font-semibold">
          Submeta sua evidência para concluir esta missão
        </p>
      </div>

      {submission && (
        <div
          className={cn('p-4 rounded-lg border', {
            'bg-yellow-50 border-yellow-200': submission.status === 'pending',
            'bg-green-50 border-green-200': submission.status === 'approved',
            'bg-red-50 border-red-200': submission.status === 'refused'
          })}
        >
          <div className="flex items-center gap-2">
            <div
              className={cn('w-2 h-2 rounded-full', {
                'bg-yellow-500': submission.status === 'pending',
                'bg-green-500': submission.status === 'approved',
                'bg-red-500': submission.status === 'refused'
              })}
            />
            <span className="font-medium text-sm">
              {submission.status === 'pending' && 'Aguardando aprovação'}
              {submission.status === 'approved' && 'Aprovado'}
              {submission.status === 'refused' && 'Recusado - Tente novamente'}
            </span>
          </div>
          {/* Motivo escrito pelo avaliador. Sem ele o aluno reenvia às cegas. */}
          {submission.status === 'refused' && submission.review_feedback ? (
            <p className="mt-2 text-sm text-red-800 whitespace-pre-wrap break-words">
              {submission.review_feedback}
            </p>
          ) : null}
        </div>
      )}

      {canSubmit && (
        <FormProvider {...(form as any)}>
          <form
            onSubmit={form.handleSubmit(handleSubmitContent)}
            className="flex flex-col gap-4"
          >
            {evidenceKind === 'link' && (
              <FormItem name="content">
                <FormItem.Label required>Link de resposta</FormItem.Label>
                <FormItem.Control>
                  <Input
                    name="content"
                    type="url"
                    placeholder="https://exemplo.com"
                    required
                  />
                </FormItem.Control>
              </FormItem>
            )}

            {evidenceKind === 'text' && (
              <FormItem name="content">
                <FormItem.Label required>Texto de resposta</FormItem.Label>
                <FormItem.Control>
                  <Textarea
                    name="content"
                    placeholder="Descreva sua evidência aqui..."
                    rows={4}
                    required
                    className="bg-white"
                  />
                </FormItem.Control>
              </FormItem>
            )}

            {evidenceKind === 'image' && (
              <FormItem name="media_id">
                <FormItem.Label required>Imagem</FormItem.Label>
                <FormItem.Control>
                  <ImageInput mediaApiScope="user" />
                </FormItem.Control>
              </FormItem>
            )}

            <div className="flex flex-col gap-3">
              {evidenceKind === 'qr_code' ? (
                <Button
                  type="button"
                  onClick={onOpenCamera}
                  variant="primary"
                  className="w-full"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  Abrir câmera
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Concluindo...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Concluir
                    </>
                  )}
                </Button>
              )}
            </div>
          </form>
        </FormProvider>
      )}
    </Box>
  )
}
